import {
  createPlatformObjectJsonSchema,
  isPlatformObjectSchema,
  PlatformJsonSchema,
} from 'dashboard/lib/workflows/platform-object';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import {
  isJsonSchemaArray,
  isJsonSchemaObject,
  JsonSchema,
  JsonSchemaString,
  JsonSchemaTypes,
} from 'lib/json-schema';
import { generateWorkflowToken } from 'lib/workflows';

const replaceIds = (schema: PlatformJsonSchema): PlatformJsonSchema => {
  return {
    ...schema,
    id: generateWorkflowToken(),
    ...('properties' in schema
      ? {
          properties: Object.entries(schema.properties).reduce((acc, [key, value]) => {
            return {
              ...acc,
              [key]: replaceIds(value),
            };
          }, {}),
        }
      : {}),
    ...('items' in schema && 'properties' in schema.items
      ? {
          items: {
            ...schema.items,
            properties: Object.entries(schema.items.properties).reduce((acc, [key, value]) => {
              return {
                ...acc,
                [key]: replaceIds(value),
              };
            }, {}),
          },
        }
      : {}),
  };
};

export const duplicateProperty = (
  propertyName = 'property',
  schema: PlatformJsonSchema,
): { id: string; name: string; schema: PlatformJsonSchema } => {
  return {
    ...createNewProperty(propertyName),
    schema: replaceIds(schema),
  };
};

export const createNewProperty = (
  propertyName = 'property',
): { id: string; name: string; schema: JsonSchemaString } => {
  const id = generateWorkflowToken();
  return {
    id,
    name: `${propertyName}_${Date.now().toString().slice(-4)}`,
    schema: { type: JsonSchemaTypes.String, id },
  };
};

export const hasNesting = (schema: JsonSchema) =>
  (isJsonSchemaObject(schema) && !isPlatformObjectSchema(schema)) ||
  (isJsonSchemaArray(schema) &&
    isJsonSchemaObject(schema.items) &&
    !isPlatformObjectSchema(schema.items));

export const getDefaultSchemaForType = (
  type: JsonSchemaTypes | WorkflowFieldObjectKey,
  template?: string,
): PlatformJsonSchema => {
  switch (type) {
    case JsonSchemaTypes.Boolean:
    case JsonSchemaTypes.Integer:
    case JsonSchemaTypes.Number:
    case JsonSchemaTypes.String:
      return { type };
    case JsonSchemaTypes.Object: {
      const newProp = createNewProperty();
      return {
        type: JsonSchemaTypes.Object,
        properties: { [newProp.name]: newProp.schema },
      };
    }
    case JsonSchemaTypes.Array:
      return { type: JsonSchemaTypes.Array, items: { type: JsonSchemaTypes.String } };
    default: {
      return createPlatformObjectJsonSchema(type, template);
    }
  }
};

export const isJsonSchemaType = (type: string): type is JsonSchemaTypes => {
  return Object.values(JsonSchemaTypes).includes(type as JsonSchemaTypes);
};
